import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { Shell } from '../../../componentes/Shell.jsx';
import { BandaDeRestauracion, MarcaDeBorrador } from '../../../nucleo/borradores/BandaDeBorrador.jsx';
import { FORMULARIOS_CON_BORRADOR } from '../../../nucleo/borradores/almacenDeBorradores.js';
import { useBorrador } from '../../../nucleo/borradores/useBorrador.js';
import { formatearFecha, obtenerFechaDeNegocioHoy } from '../../../nucleo/fecha/fechaLocal.js';
import { CintaDeVersionNueva } from '../../../nucleo/pwa/CintaDeVersionNueva.jsx';
import { useSesion } from '../../../nucleo/sesion/ProveedorDeSesion.jsx';
import { obtenerMovimiento } from '../../movimientos/api/movimientosApi.js';
import { generarPlan, simularPlan } from '../api/msiApi.js';
import AvisoDeError from '../../../nucleo/avisos/AvisoDeError.jsx';
import { CalendarioPlegadoPorAnio } from '../componentes/CalendarioPlegadoPorAnio.jsx';
import {
  importe as formatearImporte, MESES_FRECUENTES, MESES_MAXIMO, MESES_MINIMO,
} from '../formato.js';

/**
 * Los renglones que el pliegue necesita, con lo que ya vino resuelto del servidor
 * (RNF-038). En un plan que todavía no existe nada está cargado y no hay «la que
 * sigue»: el desglose solo agrupa por año y suma lo que ese año pliega.
 */
function renglonesDelDesglose(desglose) {
  return desglose.map((mensualidad) => ({
    clave: mensualidad.numeroDeMensualidad,
    fechaCorte: mensualidad.fechaCorte,
    importe: mensualidad.importe,
    estaCargada: false,
    esHistorica: false,
    esLaQueSigue: false,
    esLaDelResiduo: Boolean(mensualidad.esLaQueAbsorbeElResiduo),
    origen: mensualidad,
  }));
}

/**
 * Generar el plan de un gasto ya capturado (RF-064, RF-065, RF-066).
 *
 * EL NÚMERO DE MESES ES EL ÚNICO DATO QUE LA PERSONA CAPTURA EN TODO EL MÓDULO. No
 * se pide tasa ni comisión —un plan a meses SIN intereses no las tiene (S-31,
 * S-33)—, no se recaptura el importe y no se manda ninguna fecha: el calendario lo
 * calcula el 2/6, que es la única implementación del recorrido a fin de mes (D-56,
 * RNF-035).
 *
 * EL DESGLOSE COMPLETO SE MUESTRA ANTES DE CONFIRMAR (RF-066), con la última
 * mensualidad señalada SOLO CUANDO DE VERDAD DIFIERE —lo dice el servidor con
 * `esLaQueAbsorbeElResiduo`—, para que nadie descubra el centavo en el estado de
 * cuenta. La pantalla NO divide el importe: pide el plan y muestra lo que responde.
 */
const MESES_PROPUESTOS = 12;

export default function GenerarPlan() {
  const { idMovimiento } = useParams();
  const navegar = useNavigate();
  const { sesion } = useSesion();
  const hoy = obtenerFechaDeNegocioHoy();

  const [gasto, setGasto] = useState(null);
  const [meses, setMeses] = useState(MESES_PROPUESTOS);
  const [nota, setNota] = useState('');
  const [error, setError] = useState(null);
  const [guardando, setGuardando] = useState(false);
  const [desglose, setDesglose] = useState(null);
  const [confirmandoCancelacion, setConfirmandoCancelacion] = useState(false);

  // Cambiar el número de meses invalida el desglose: no se confirma nunca un
  // reparto que ya no corresponde a lo que se está pidiendo.
  useEffect(() => { setDesglose(null); }, [meses]);

  /*
   * BORRADOR DE LA CAPTURA DEL PLAN (RF-084, RN-128, RN-144b).
   *
   * Clave propia —PLAN_MSI más el gasto que se está difiriendo—, así que NO se pisa
   * con el borrador de Nueva cuenta: son formularios distintos y viven aparte.
   *
   * El importe NO se captura aquí (es el del gasto) y el desglose no se guarda: se
   * vuelve a pedir al servidor, que es la única implementación del reparto (D-56,
   * RNF-038).
   */
  const valores = useMemo(() => ({ meses: String(meses), nota }), [meses, nota]);
  const iniciales = useMemo(() => ({ meses: String(MESES_PROPUESTOS), nota: '' }), []);

  const restaurar = useCallback((guardados) => {
    setMeses(guardados.meses ?? MESES_PROPUESTOS);
    setNota(guardados.nota ?? '');
  }, []);

  const borrador = useBorrador({
    formulario: FORMULARIOS_CON_BORRADOR.PLAN_MSI,
    idRegistro: idMovimiento,
    idUsuario: sesion?.idUsuario ?? null,
    valores,
    valoresIniciales: iniciales,
    alRestaurar: restaurar,
  });

  function cancelar() {
    if (borrador.sucio) {
      setConfirmandoCancelacion(true);
      return;
    }
    /* RF-148, RN-391: los DOS cancelar REEMPLAZAN el paso. Lo que ya estaba bien
       en esta pantalla —el regreso tras generar el plan, que ya reemplazaba— NO se
       toca: el barrido es para los que empujan, no para uniformar lo que cumple
       (RN-392). */
    navegar(`/movimientos/${idMovimiento}`, { replace: true });
  }

  function confirmarCancelacion() {
    borrador.borrarPorCancelacion();
    setConfirmandoCancelacion(false);
    navegar(`/movimientos/${idMovimiento}`, { replace: true });
  }

  /**
   * EL DESGLOSE COMPLETO ANTES DE CONFIRMAR (RF-066). Lo calcula EL SERVIDOR con la
   * misma pieza que usará al guardar: esta pantalla no divide ni un importe
   * (RNF-038, prueba 59).
   */
  async function verDesglose() {
    setError(null);
    setGuardando(true);
    try {
      setDesglose(await simularPlan({
        idMovimiento: Number(idMovimiento),
        numeroDeMeses: Number(meses),
        fechaLocal: hoy,
      }));
    } catch (fallo) {
      setError(fallo);
    } finally {
      setGuardando(false);
    }
  }

  useEffect(() => {
    (async () => {
      try {
        setGasto((await obtenerMovimiento(idMovimiento)).movimiento);
      } catch (fallo) {
        setError(fallo);
      }
    })();
  }, [idMovimiento]);

  async function confirmar() {
    setError(null);
    setGuardando(true);
    try {
      const plan = await generarPlan({
        idMovimiento: Number(idMovimiento),
        numeroDeMeses: Number(meses),
        fechaLocal: hoy,
        nota: nota || null,
      });
      // RN-134a: al guardar con éxito, el borrador de este dispositivo se borra.
      borrador.borrarPorGuardado();
      navegar(`/cuentas/meses/planes/${plan.idPlan}`, { replace: true });
    } catch (fallo) {
      setError(fallo);
    } finally {
      setGuardando(false);
    }
  }

  return (
    <Shell titulo="Generar plan de meses" volverA={`/movimientos/${idMovimiento}`}>
      {/* Versión nueva en espera mientras se captura (RF-085, RN-131). */}
      <CintaDeVersionNueva />

      <BandaDeRestauracion
        restauradoHace={borrador.restauradoHace}
        alDescartar={borrador.descartarBorrador}
      />

      {confirmandoCancelacion && (
        <div className="aviso w" role="status" data-prueba="confirmar-cancelacion">
          <b>¿Descartar lo capturado?</b> Cancelar borra el borrador guardado en este dispositivo y
          <b> pierdes lo escrito</b>. Salir sin cancelar lo conserva.
          <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
            <button className="btn" type="button" onClick={confirmarCancelacion} data-prueba="confirmar-descartar">
              Sí, descartar
            </button>
            <button className="btn sec" type="button" onClick={() => setConfirmandoCancelacion(false)}>
              Seguir capturando
            </button>
          </div>
        </div>
      )}

      <AvisoDeError error={error} />

      {gasto && (
        <div className="card" style={{ margin: 12 }}>
          <div className="fila"><span className="crece">Compra</span><b>{gasto.nota || gasto.nombreCategoria}</b></div>
          <div className="fila"><span className="crece">Fecha</span><b>{formatearFecha(gasto.fecha)}</b></div>
          <div className="fila"><span className="crece">Tarjeta</span><b>{gasto.nombreCuenta}</b></div>
          <div className="fila">
            <span className="crece">Importe de la compra</span>
            <b>{formatearImporte(gasto.importe, gasto.codigoISO)}</b>
          </div>
          {/* Fila 25 del inventario del incremento 15 (RF-223, CA-15-02): salió el código
              interno del proyecto «(S-31)»; el resto del texto se conserva. */}
          <span className="ayuda" data-prueba="generar-plan-importe-ayuda">
            El importe no se recaptura: es el del gasto. <b>Un plan a meses sin intereses no lleva tasa
            ni comisión</b>: si el banco te cobra alguna, regístrala aparte como un gasto con
            esta misma tarjeta.
          </span>
        </div>
      )}

      <div className="card" style={{ margin: 12 }}>
        <div className="campo">
          <label htmlFor="meses">NÚMERO DE MESES</label>
          <div data-prueba="atajos-de-meses">
            {MESES_FRECUENTES.map((cantidad) => (
              <button
                key={cantidad}
                className="btn sec"
                type="button"
                aria-pressed={Number(meses) === cantidad}
                onClick={() => setMeses(cantidad)}
              >
                {cantidad}
              </button>
            ))}
          </div>
          <input
            id="meses"
            name="meses"
            type="number"
            min={MESES_MINIMO}
            max={MESES_MAXIMO}
            value={meses}
            onChange={(evento) => setMeses(evento.target.value)}
          />
          {/* Literal asentado en RN-766 con el prototipo aprobado v45: la cota sale
              de la constante, nunca escrita a mano. */}
          <span className="ayuda">
            De {MESES_MINIMO} a {MESES_MAXIMO}. Toca un plazo de arriba o escribe el tuyo.
          </span>
        </div>

        <div className="campo">
          <label htmlFor="nota">NOTA (OPCIONAL)</label>
          <input id="nota" name="nota" type="text" maxLength={500} value={nota}
            onChange={(evento) => setNota(evento.target.value)} />
        </div>

        <button className="btn ancho" type="button" disabled={guardando} onClick={verDesglose}>
          Ver el desglose
        </button>

        <button className="btn sec ancho" type="button" onClick={cancelar}>
          Cancelar
        </button>

        <MarcaDeBorrador sucio={borrador.sucio} />

        {/* Fila 26 del inventario del incremento 15 (RF-222): salió la justificación de la
            implementación («esta pantalla no las calcula»). */}
        <span className="ayuda" data-prueba="generar-plan-calculo-ayuda">
          El reparto y las fechas se calculan solos, con el día de corte y la fecha límite de tu
          tarjeta.
        </span>
      </div>

      {desglose && (
        <div className="card" style={{ margin: 12 }} data-prueba="desglose-del-plan">
          <h4 style={{ margin: '0 0 6px', fontSize: 11.5 }}>DESGLOSE ANTES DE CONFIRMAR</h4>

          {/* PLIEGUE POR AÑO EN LOS PLAZOS LARGOS (RF-274, RN-767): con 24
              mensualidades o menos el desglose se pinta de corrido, igual que
              siempre. Arriba de 24 se agrupa por año y abre el primero. El estado
              agregado no se pinta aquí a propósito: este plan todavía no existe,
              así que todo está por caer. */}
          <CalendarioPlegadoPorAnio
            renglones={renglonesDelDesglose(desglose)}
            codigoISO={gasto?.codigoISO}
            datoDePrueba="desglose-del-plan-lista"
            mostrarEstadoAgregado={false}
            pintarRenglon={(renglon) => {
              const mensualidad = renglon.origen;

              return (
                <div className="fila" key={mensualidad.numeroDeMensualidad}>
                  <div className="crece">
                    {/* CONTADOR YA ARMADO POR EL SERVIDOR (RNF-038). */}
                    <b data-prueba="contador">{mensualidad.contadorDeMensualidad}</b>
                    <small>
                      corte {formatearFecha(mensualidad.fechaCorte)} · pago a más tardar el{' '}
                      {formatearFecha(mensualidad.fechaLimitePago)}
                    </small>
                  </div>
                  <span className="imp">
                    {formatearImporte(mensualidad.importe, gasto?.codigoISO)}
                    {/* Se señala SOLO cuando de verdad difiere: lo dice el servidor
                        con esLaQueAbsorbeElResiduo (RN-100, prueba 3). */}
                    {mensualidad.esLaQueAbsorbeElResiduo && (
                      <small data-prueba="absorbe-residuo">lleva los centavos que sobran</small>
                    )}
                  </span>
                </div>
              );
            }}
          />

          <button className="btn ancho" type="button" disabled={guardando} onClick={confirmar}>
            Generar el plan
          </button>
        </div>
      )}
    </Shell>
  );
}
